
export function formatCurrency(normalized: string, currency = "EUR") {
  if (!/^\d+(\.\d+)?$/.test(normalized)) return `${currency} ${normalized}`;
  const [whole, fraction = "00"] = normalized.split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return currency === "EUR" ? `€ ${grouped}.${fraction.padEnd(2, "0").slice(0, 2)}` : `${currency} ${grouped}.${fraction.padEnd(2, "0").slice(0, 2)}`;
}

export function formatDate(value?: string | null) {
  if (!value) return "Not scheduled";
  const parsed = new Date(value.includes("T") ? value : `${value}T12:00:00+02:00`);
  if (Number.isNaN(parsed.valueOf())) return value;
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Amsterdam" }).format(parsed);
}

function toCents(value: string): bigint {
  if (!/^\d+(\.\d{1,2})?$/.test(value)) throw new Error("invalid_money");
  const [whole, fraction = "00"] = value.split(".");
  const normalizedFraction = fraction.padEnd(2, "0").slice(0, 2);
  return BigInt(whole) * 100n + BigInt(normalizedFraction);
}

function fromCents(value: bigint): string {
  const sign = value < 0n ? "-" : "";
  const absolute = value < 0n ? -value : value;
  return `${sign}${absolute / 100n}.${String(absolute % 100n).padStart(2, "0")}`;
}

/** Decimal-safe (cents-based) sum of money strings like "352.00". Used for
 * the combined planning view so repeated float rounding never sneaks in. */
export function addMoney(...values: string[]): string {
  const totalCents = values.reduce((sum, value) => sum + toCents(value), 0n);
  return fromCents(totalCents);
}

/** Decimal-safe (cents-based) subtraction of money strings like "352.00". */
export function subtractMoney(minuend: string, subtrahend: string): string {
  return fromCents(toCents(minuend) - toCents(subtrahend));
}

// --- W4-04: item timing ------------------------------------------------

const MONTH_YEAR = new Intl.DateTimeFormat("en-GB", {
  month: "long",
  year: "numeric",
  timeZone: "Europe/Amsterdam",
});
const MONTH_ONLY = new Intl.DateTimeFormat("en-GB", {
  month: "long",
  timeZone: "Europe/Amsterdam",
});
const QUARTER_NAMES = ["first", "second", "third", "fourth"];

function parseCivilDate(value: string): Date {
  return new Date(`${value}T12:00:00+02:00`);
}

function yearOf(value: string): number {
  return parseCivilDate(value).getFullYear();
}

/**
 * The date part of a timing sentence, honouring `precision` structurally:
 * there is no code path here from `"month"` or `"quarter"` to a
 * day-level string. This is the client-side half of the no-false-precision
 * guarantee — see docs/design/features/W4-04.md, "The one thing this
 * design has to get right".
 */
export function formatTimingWindow(
  start: string,
  end: string | null,
  precision: "day" | "month" | "quarter" | "none",
): string {
  if (precision === "day") return formatDate(start);
  if (precision === "quarter") {
    const quarterIndex = Math.floor(parseCivilDate(start).getMonth() / 3);
    return `the ${QUARTER_NAMES[quarterIndex] ?? "next"} quarter of ${yearOf(start)}`;
  }
  // Month precision. A single month, or a window spanning several.
  if (!end || MONTH_ONLY.format(parseCivilDate(start)) === MONTH_ONLY.format(parseCivilDate(end))) {
    return MONTH_YEAR.format(parseCivilDate(start));
  }
  const startLabel = yearOf(start) === yearOf(end) ? MONTH_ONLY.format(parseCivilDate(start)) : MONTH_YEAR.format(parseCivilDate(start));
  return `${startLabel}–${MONTH_YEAR.format(parseCivilDate(end))}`;
}

const CADENCE_WORDS: Record<string, string> = {
  monthly: "Every month",
  quarterly: "Every quarter",
  annual: "Every year",
};

/**
 * The one sentence `TimingLine` renders for a declared timing. Always
 * built from the item's own declared fields (`kind`/`window_start`/
 * `window_end`/`precision`/`description`/`cadence`) — never from
 * `effective`, which is what scheduling reads and is rendered separately,
 * alongside this line, when a user's own date is on record. See
 * docs/design/features/W4-04.md, "UI/component delta".
 */
export function formatTiming(timing: {
  kind: string;
  precision: "day" | "month" | "quarter" | "none";
  window_start: string | null;
  window_end: string | null;
  cadence?: string | null;
  description?: string | null;
}): string {
  const { kind, precision, window_start: start, window_end: end, cadence, description } = timing;

  if (kind === "unknown" || !start) {
    // `event_triggered` is the one declarable kind that can reach here
    // with no date: it stays visible via its own server-owned sentence,
    // never a placeholder like "—" or "Unknown".
    return description || "We don't know when this happens yet";
  }

  if (kind === "fixed_date") return `On ${formatTimingWindow(start, end, precision)}`;

  if (kind === "admin_deadline") {
    return precision === "day"
      ? `Deadline: ${formatTimingWindow(start, end, precision)}`
      : `Deadline in ${formatTimingWindow(start, end, precision)}`;
  }

  if (kind === "expected_window") {
    return precision === "quarter"
      ? `Usually in ${formatTimingWindow(start, end, precision)}`
      : `Usually ${formatTimingWindow(start, end, precision)}`;
  }

  if (kind === "recurring") {
    const cadenceWord = (cadence && CADENCE_WORDS[cadence]) || "Recurring";
    return `${cadenceWord} — next in ${formatTimingWindow(start, end, precision)}`;
  }

  if (kind === "event_triggered") {
    const dateLabel = formatTimingWindow(start, end, precision);
    return description ? `${description} — by ${dateLabel}` : `By ${dateLabel}`;
  }

  // user_deadline never reaches here as a top-level declared kind — see
  // docs/architecture/adr/ADR-0008-item-timing-contract.md decision 8 —
  // but a structurally unreachable case still degrades rather than throws.
  return description || formatTimingWindow(start, end, precision);
}

// --- W5-02: reminder controls, defaults and user-facing email UX -------

/**
 * "on {date}" for day precision, "in {Month YYYY}" for month precision,
 * "in the {quarter} quarter of {year}" for quarter precision — the one
 * phrase every reminder-related date on screen goes through (the card
 * line, the detail section's "We'll email you …" and "Last reminder sent
 * …" sentences). Reuses `formatTimingWindow`'s own quarter/month
 * machinery rather than a second implementation, so there is exactly one
 * place in this file that knows how to turn a quarter index into words.
 *
 * Structurally, there is no code path here from `"month"` or `"quarter"`
 * to a day-level string — the same guarantee `formatTimingWindow` already
 * gives the declared-timing line, restated here at the point where
 * breaking it is most expensive: an unsolicited message, about someone's
 * money, at our initiative. See docs/design/features/W5-02.md, "The one
 * thing this design has to get right".
 */
export function formatReminderWhen(on: string, precision: "day" | "month" | "quarter" | "none"): string {
  if (precision === "day") return `on ${formatDate(on)}`;
  if (precision === "quarter") {
    const quarterIndex = Math.floor(parseCivilDate(on).getMonth() / 3);
    return `in the ${QUARTER_NAMES[quarterIndex] ?? "next"} quarter of ${yearOf(on)}`;
  }
  // "month", and "none" as a defensive fallback — a reminder occurrence's
  // own precision is always day/month/quarter in practice (the server
  // never derives one for a "none"-precision item, since that means "this
  // never happens"), but the field's declared type is the shared
  // `TimingPrecision` union, so this stays total rather than throwing on
  // a value that should be structurally unreachable.
  return `in ${MONTH_YEAR.format(parseCivilDate(on))}`;
}



